if (!customElements.get('pickup-availability')) {
  /**
   * In-memory cache for pickup availability data
   * Prevents excessive API calls and improves performance
   * @type {Map<string, {data: HTMLElement, timestamp: number}>}
   */
  const pickupCache = new Map();
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  const MAX_CACHE_SIZE = 50; // Prevent memory issues

  /**
   * Custom element for displaying product pickup availability
   * Features: Caching, debouncing, loading states, Google Maps integration
   * @extends HTMLElement
   */
  class PickupAvailability extends HTMLElement {
    constructor() {
      super();
      if (!this.hasAttribute('available')) return;

      this.refreshHandler = this.handleRefreshBtnClick.bind(this);
      this.unavailableHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
      this.debounceTimer = null;
      this.isLoading = false;

      // Check if content is already server-side rendered (progressive enhancement)
      const existingContent = this.querySelector('.pickup-status');

      if (existingContent) {
        // Content already rendered server-side - just enhance with interactivity
        this.setupInteractivity();
      } else {
        // No server-side content - fetch via AJAX (fallback for variant changes)
        this.getAvailabilityDebounced(this.dataset.variantId);
      }
    }

    /**
     * Enhances server-side rendered content with interactivity.
     * Called when content is already present (no AJAX fetch needed).
     * @returns {void}
     */
    setupInteractivity() {
      // Apply readiness message customization
      this.applyReadinessSettings();

      // Check if drawer already exists (might be from previous variant)
      const existingDrawer = document.querySelector('.js-pickup-drawer');

      if (!existingDrawer) {
        // Drawer doesn't exist yet - fetch ONLY drawer without replacing content
        this.fetchDrawerOnly(this.dataset.variantId);
      } else {
        // Drawer exists - just set up button click handler
        const showDetailsBtn = this.querySelector('.js-show-pickup-info');
        if (showDetailsBtn) {
          showDetailsBtn.addEventListener('click', () => {
            existingDrawer.open(showDetailsBtn);
          });
        }
      }
    }

    /**
     * Fetches only the drawer section without replacing existing pickup-status content.
     * Used when content is already server-side rendered.
     * @param {string} variantId - Current variant id.
     * @returns {Promise<void>}
     */
    async fetchDrawerOnly(variantId) {
      const { rootUrl } = this.dataset;
      if (!rootUrl || !variantId || variantId === 'undefined') return;

      const baseUrl = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;

      try {
        const response = await fetch(`${baseUrl}variants/${variantId}/?section_id=pickup-availability`, {
          headers: { 'Cache-Control': 'public, max-age=300' }
        });

        if (!response.ok) return;

        const text = await response.text();
        const tmpl = document.createElement('template');
        tmpl.innerHTML = text;
        const sectionHtml = tmpl.content.querySelector('.shopify-section');

        if (!sectionHtml) return;

        // Only extract and append the drawer, don't touch pickup-status
        const drawer = sectionHtml.querySelector('.js-pickup-drawer');
        if (drawer) {
          document.body.appendChild(drawer);

          // Set up button click handler
          const showDetailsBtn = this.querySelector('.js-show-pickup-info');
          if (showDetailsBtn) {
            showDetailsBtn.addEventListener('click', () => {
              drawer.open(showDetailsBtn);
            });
          }

          // Apply readiness settings to drawer
          this.applyReadinessSettings();

          // Apply Google Maps card settings
          this.applyGoogleMapsCard();
        }
      } catch (error) {
        console.error('Error fetching drawer:', error);
      }
    }

    /**
     * Handles 'click' events on the refresh button.
     * Clears cache and fetches fresh pickup availability data.
     * @returns {void}
     */
    handleRefreshBtnClick() {
      // Clear cache for this variant and fetch fresh data
      pickupCache.delete(this.dataset.variantId);
      this.getAvailability(this.dataset.variantId);
    }

    /**
     * Debounced version of getAvailability to prevent request flooding.
     * Waits 300ms after last variant change before fetching.
     * @param {string} variantId - Current variant id.
     * @returns {void}
     */
    getAvailabilityDebounced(variantId) {
      // Clear existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Set new timer
      this.debounceTimer = setTimeout(() => {
        this.getAvailability(variantId);
      }, 300); // Wait 300ms after last change
    }

    /**
     * Gets pick up availability for the current variant.
     * Implements caching, loading states, and error handling.
     * @param {string} variantId - Current variant id.
     * @returns {Promise<void>}
     */
    async getAvailability(variantId) {
      // Prevent duplicate requests
      if (this.isLoading) return;

      // Validate variant ID
      if (!variantId || variantId === 'undefined') {
        this.renderUnavailableMsg();
        return;
      }

      // Check cache first
      const cached = pickupCache.get(variantId);
      if (cached && (Date.now() - cached.timestamp < CACHE_TTL)) {
        this.renderPickupInfo(cached.data);
        return;
      }

      const { rootUrl } = this.dataset;
      if (!rootUrl) {
        console.error('Pickup availability: rootUrl not configured');
        this.renderUnavailableMsg();
        return;
      }

      const baseUrl = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;

      try {
        this.isLoading = true;
        this.showLoadingState();

        const response = await fetch(`${baseUrl}variants/${variantId}/?section_id=pickup-availability`, {
          headers: {
            'Cache-Control': 'public, max-age=300'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const text = await response.text();
        const tmpl = document.createElement('template');
        tmpl.innerHTML = text;
        const sectionHtml = tmpl.content.querySelector('.shopify-section');

        if (!sectionHtml) {
          throw new Error('Invalid response: .shopify-section not found');
        }

        // Manage cache size - remove oldest entries if cache is too large
        if (pickupCache.size >= MAX_CACHE_SIZE) {
          const oldestKey = pickupCache.keys().next().value;
          pickupCache.delete(oldestKey);
        }

        // Cache the successful response
        pickupCache.set(variantId, {
          data: sectionHtml.cloneNode(true),
          timestamp: Date.now()
        });

        this.renderPickupInfo(sectionHtml);
      } catch (error) {
        // Log error for debugging but show user-friendly message
        console.error('Pickup availability error:', error);
        this.renderUnavailableMsg();
      } finally {
        this.isLoading = false;
      }
    }

    /**
     * Shows a loading skeleton while fetching data.
     * Includes ARIA attributes for accessibility.
     * @returns {void}
     */
    showLoadingState() {
      // Only show if element is currently empty or has error state
      if (!this.querySelector('.pickup-status')) {
        this.setAttribute('aria-busy', 'true');
        this.innerHTML = `
          <div class="pickup-loading" role="status" aria-live="polite">
            <div class="pickup-skeleton">
              <div class="skeleton-line"></div>
              <div class="skeleton-line short"></div>
            </div>
            <span class="visually-hidden">Loading pickup information...</span>
          </div>
        `;
      }
    }

    /**
     * Renders the pickup availability information in the UI.
     * Replaces loading skeleton with actual pickup data and drawer.
     * @param {HTMLElement} sectionHtml - Fetched section HTML containing pickup data.
     * @returns {void}
     */
    renderPickupInfo(sectionHtml) {
      // Clear loading state
      this.removeAttribute('aria-busy');

      const drawer = document.querySelector('.js-pickup-drawer');
      if (drawer) drawer.remove();

      if (!sectionHtml.querySelector('.pickup-status')) {
        this.innerHTML = '';
        this.removeAttribute('available');
        return;
      }

      this.setAttribute('available', '');
      this.innerHTML = sectionHtml.querySelector('.pickup-status').outerHTML;
      document.body.appendChild(sectionHtml.querySelector('.js-pickup-drawer'));

      // Apply readiness message settings
      this.applyReadinessSettings();

      // Apply Google Maps card
      this.applyGoogleMapsCard();

      const showDetailsBtn = this.querySelector('.js-show-pickup-info');
      if (showDetailsBtn) {
        showDetailsBtn.addEventListener('click', () => {
          document.querySelector('.js-pickup-drawer').open(showDetailsBtn);
        });
      }
    }

    /**
     * Applies readiness message settings from data attributes.
     * Controls visibility and content of "Usually ready in X" messages.
     * Supports Shopify default or custom messages.
     * @returns {void}
     */
    applyReadinessSettings() {
      // Parse settings from data attributes
      // Note: dataset values are always strings, so we need to compare against string 'true'/'false'
      const showReadiness = this.dataset.showReadiness !== 'false';
      const readinessSource = this.dataset.readinessSource || 'shopify';
      const customMessage = this.dataset.customMessage || 'Usually ready in 5+ days';

      // Find readiness message in main pickup status
      // Note: this.innerHTML is already the .pickup-status div, so we don't need to search for it
      const readinessMsg = this.querySelector('p.text-sm');

      if (readinessMsg) {
        if (!showReadiness) {
          // Hide the readiness message
          readinessMsg.style.display = 'none';
        } else if (readinessSource === 'custom') {
          // Replace with custom message
          readinessMsg.textContent = customMessage;
        } else {
          // Ensure it's visible for Shopify source
          readinessMsg.style.display = '';
        }
      }

      // Apply same settings to drawer/popup
      const drawer = document.querySelector('.js-pickup-drawer');
      if (drawer) {
        // Find all location items in the drawer
        const locationItems = drawer.querySelectorAll('li');

        locationItems.forEach(item => {
          // Find the paragraph that contains pickup time info (inside .flex div with pickup-icon)
          const flexDiv = item.querySelector('.flex');
          if (!flexDiv) return;

          const pickupInfoP = flexDiv.querySelector('p');
          if (!pickupInfoP) return;

          const text = pickupInfoP.textContent.trim().toLowerCase();

          // Check if this paragraph contains pickup time/availability information
          // It typically contains "pick up available" or "usually ready"
          if (text.includes('pick up available') || text.includes('usually ready')) {
            // Store original text if not already stored
            if (!pickupInfoP.dataset.originalText) {
              pickupInfoP.dataset.originalText = pickupInfoP.textContent;
            }

            if (!showReadiness) {
              // Split the text and only show "Pick up available" part, hide the time
              const parts = pickupInfoP.dataset.originalText.split(',');
              if (parts.length > 1) {
                pickupInfoP.textContent = parts[0]; // Keep only "Pick up available"
              }
            } else if (readinessSource === 'custom') {
              // Replace the time portion with custom message
              const parts = pickupInfoP.dataset.originalText.split(',');
              if (parts.length > 1) {
                pickupInfoP.textContent = `${parts[0]}, ${customMessage.toLowerCase()}`;
              }
            } else {
              // Restore original Shopify text
              pickupInfoP.textContent = pickupInfoP.dataset.originalText;
            }
          }
        });
      }
    }

    /**
     * Sanitizes text to prevent XSS attacks
     * @param {string} text - Text to sanitize
     * @returns {string} - Sanitized text
     */
    sanitize(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Creates and injects Google Maps card into pickup drawer.
     * Includes store image, rating, and action buttons (Call, WhatsApp, Directions).
     * Uses secure template cloning to prevent XSS vulnerabilities.
     * @returns {void}
     */
    applyGoogleMapsCard() {
      const showMapsCard = this.dataset.showMapsCard !== 'false';
      const mapsUrl = this.dataset.mapsUrl;
      const storeRating = this.dataset.storeRating;
      const reviewCount = this.dataset.reviewCount;
      const whatsappNumber = this.dataset.whatsappNumber;
      const productTitle = this.dataset.productTitle;
      const productSku = this.dataset.productSku;
      const productUrl = this.dataset.productUrl;

      // Only create card if enabled and we have a Maps URL
      if (!showMapsCard || !mapsUrl) return;

      const drawer = document.querySelector('.js-pickup-drawer');
      if (!drawer) return;

      // Get the template (rendered by theme's image snippet in Liquid)
      const template = document.getElementById('google-maps-card-template');
      if (!template) {
        console.error('Google Maps card template not found');
        return;
      }

      // Create WhatsApp message with product context
      const whatsappMessage = `Hi! I would like to get pickup information about:

${productTitle}
SKU: ${productSku}

${productUrl}`;
      const encodedMessage = encodeURIComponent(whatsappMessage);

      // Find all Google Maps card containers in the drawer
      const containers = drawer.querySelectorAll('.google-maps-card-container');

      containers.forEach(container => {
        // Clone the template (image already rendered by Liquid with theme's snippet)
        const card = template.content.cloneNode(true);

        // Update image link href (image itself is already rendered by Liquid)
        const imageLink = card.querySelector('.google-maps-card__image-link');
        if (imageLink) {
          imageLink.href = mapsUrl;
        }

        // Update header link href (makes "View on Google Maps" and rating clickable)
        const headerLink = card.querySelector('.google-maps-card__header-link');
        if (headerLink) {
          headerLink.href = mapsUrl;
        }

        // Set rating if available
        if (storeRating) {
          const rating = parseFloat(storeRating);
          const ratingContainer = card.querySelector('.google-maps-card__rating');
          const ratingText = card.querySelector('.google-maps-card__rating-text');
          const reviewCountEl = card.querySelector('.google-maps-card__review-count');

          if (ratingContainer && ratingText) {
            ratingContainer.style.display = '';
            ratingText.textContent = rating.toFixed(1);

            if (reviewCount && reviewCountEl) {
              reviewCountEl.textContent = `(${this.sanitize(reviewCount)})`;
            }

            // Update star display
            const fullStars = Math.floor(rating);
            const hasHalfStar = rating % 1 >= 0.5;
            const stars = card.querySelectorAll('.star');

            stars.forEach((star, index) => {
              if (index >= fullStars && !(index === fullStars && hasHalfStar)) {
                star.setAttribute('fill', '#E0E0E0');
              } else if (index === fullStars && hasHalfStar) {
                // Half star
                star.querySelector('path').setAttribute('d',
                  'M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4V6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z'
                );
              }
            });
          }
        }

        // Set Call Now button
        const callBtn = card.querySelector('.google-maps-card__action--call');
        if (callBtn && whatsappNumber) {
          const telLink = whatsappNumber.replace(/\D/g, ''); // Remove non-digits
          callBtn.href = `tel:+${telLink}`;
          callBtn.style.display = '';
        }

        // Set WhatsApp button
        const whatsappBtn = card.querySelector('.google-maps-card__action--whatsapp');
        if (whatsappBtn && whatsappNumber) {
          whatsappBtn.href = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
          whatsappBtn.style.display = '';
        }

        // Set Get Directions button
        const directionsBtn = card.querySelector('.google-maps-card__action--directions');
        if (directionsBtn) {
          directionsBtn.href = mapsUrl;
        }

        // Append the card to container
        container.appendChild(card);
      });
    }

    /**
     * Renders a 'pick up unavailable' message.
     * Shows refresh button for retry functionality.
     * @returns {void}
     */
    renderUnavailableMsg() {
      this.innerHTML = '';
      this.appendChild(this.unavailableHtml);
      this.querySelector('.js-refresh').addEventListener('click', this.refreshHandler);
    }
  }

  customElements.define('pickup-availability', PickupAvailability);
}
